/*eslint-disable max-lines*/
import { Alert, Button, Modal, Toggle } from '@hakimo-ui/shared/ui-base';
import { GlobeAltIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import TimezoneSelect from '../timezone-select/TimezoneSelect';
import ArmedStatusForm from './ArmedStatusForm';
import {
  DisarmWindowFormValue,
  getInitialFormValue,
  getSummaryText,
  getTimeWindow,
  isDisarmWindowRelevant,
  validateDisarmWindow,
} from './util';

interface DisarmWindowPayload {
  startTimestamp: number;
  endTimestamp: number;
  timezone: string;
  discard: boolean;
  armed: boolean;
}

interface MutationState {
  isLoading: boolean;
  error?: string | null;
}

interface Props {
  onClose: () => void;
  onSubmit: (payload: DisarmWindowPayload) => void;
  mutation: MutationState;
  timezone: string;
  existingWindow?: {
    startTime: number;
    endTime: number;
    timezone?: string;
  };
  infoMessage?: string;
  allowTimezoneChange?: boolean;
  isArmed?: boolean;
}

export function DisarmWindowModal({
  onClose,
  onSubmit,
  mutation,
  timezone,
  existingWindow,
  infoMessage,
  allowTimezoneChange = false,
  isArmed,
}: Props) {
  const { isLoading, error } = mutation;
  const [isArming, setIsArming] = useState(isArmed === false);
  const isArmMode = isArmed !== undefined && isArming;
  const [currentTimezone, setCurrentTimezone] = useState(timezone);
  const effectiveTz = allowTimezoneChange ? currentTimezone : timezone;
  const isRelevantWindow = isDisarmWindowRelevant(existingWindow?.endTime);

  const [formValue, setFormValue] = useState<DisarmWindowFormValue>(() =>
    getInitialFormValue(
      effectiveTz,
      isRelevantWindow ? existingWindow : undefined
    )
  );
  const [isDiscardActive, setIsDiscardActive] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const timeWindow = getTimeWindow(formValue, effectiveTz);
  const hasValidWindow =
    timeWindow.startTimestamp !== 0 && timeWindow.endTimestamp !== 0;
  const isWindowValid =
    hasValidWindow && validateDisarmWindow(timeWindow).isValid;
  const summaryText = isWindowValid
    ? getSummaryText(formValue, effectiveTz, timeWindow, isArming)
    : '—';

  const handleSubmit = () => {
    if (isDiscardActive) {
      onSubmit({
        startTimestamp: 0,
        endTimestamp: 0,
        timezone: effectiveTz,
        discard: true,
        armed: false,
      });
      return;
    }
    const { isValid, message } = validateDisarmWindow(timeWindow);
    if (!isValid) {
      setValidationError(message);
      return;
    }
    setValidationError(null);
    onSubmit({
      ...timeWindow,
      timezone: effectiveTz,
      discard: false,
      armed: isArmMode,
    });
  };

  const footer = isDiscardActive ? (
    <>
      <Button
        variant="primary"
        onClick={handleSubmit}
        disabled={isLoading}
        loading={isLoading}
      >
        Confirm Discard
      </Button>
      <Button onClick={() => setIsDiscardActive(false)}>Back</Button>
    </>
  ) : (
    <>
      <Button
        variant="primary"
        onClick={handleSubmit}
        disabled={isLoading}
        loading={isLoading}
      >
        Submit
      </Button>
      {isRelevantWindow && !isArmMode && (
        <Button
          variant="outline"
          onClick={() => setIsDiscardActive(true)}
          disabled={isLoading}
        >
          Discard Disarm Window
        </Button>
      )}
      <Button onClick={onClose}>Cancel</Button>
    </>
  );

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={
        isArmMode
          ? 'Update Monitoring Arm Window'
          : 'Update Monitoring Disarm Window'
      }
      footer={footer}
    >
      <div className="w-[32rem] space-y-4 px-8 py-4">
        {isDiscardActive ? (
          <p className="leading-relaxed">
            This will cancel the temporary disarm period and monitoring will
            resume according to the Site Monitoring Schedule.
          </p>
        ) : (
          <>
            {!isArmMode && infoMessage && (
              <Alert type="info">{infoMessage}</Alert>
            )}
            {isArmed !== undefined && (
              <div className="flex items-center gap-3">
                <span className="text-onlight-text-2 dark:text-ondark-text-2 text-sm font-medium">
                  Arm
                </span>
                <Toggle enabled={!isArming} onChange={(v) => setIsArming(!v)} />
                <span className="text-onlight-text-2 dark:text-ondark-text-2 text-sm font-medium">
                  Disarm
                </span>
              </div>
            )}
            <ArmedStatusForm
              value={formValue}
              onChange={setFormValue}
              isArming={isArming}
            />
            {allowTimezoneChange && formValue.activeInput === 'custom' && (
              <TimezoneSelect
                value={currentTimezone}
                onChange={setCurrentTimezone}
              />
            )}
            {error && <Alert type="error">{error}</Alert>}
            {validationError && <Alert type="warning">{validationError}</Alert>}
            <div>
              <p className="text-onlight-text-2 dark:text-ondark-text-2 mb-2 text-sm font-medium tracking-wide">
                {isArmMode ? 'Arm Schedule' : 'Disarm Schedule'}
              </p>
              <div className="border-onlight-line-2 dark:border-dark-border-bg rounded-md border px-4 py-3">
                <p className="whitespace-pre-line text-sm leading-6">
                  {summaryText}
                </p>
                <p className="text-onlight-text-2 dark:text-ondark-text-2 mt-1 flex items-center gap-1 text-xs">
                  <GlobeAltIcon className="h-3 w-3" />
                  {effectiveTz}
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
